import {
  Box,
  IconButton,
  MenuItem,
  Select as MuiSelect,
  Popover,
  SvgIcon,
  Typography,
  useMediaQuery,
  FormControl,
  InputLabel,
  OutlinedInput,
} from '@mui/material'
import PropTypes from 'prop-types'
import { useCallback, useState } from 'react'
import { useController, useFormContext } from 'react-hook-form'
import { format } from 'date-fns'
import { getSiteDateFormat } from '@hapana/shared/web/utils/date-format-utils'
import { t } from 'i18next'

import ClearSearchIcon from '@hapana/shared/web/styles/assets/svgs/times-circle.svg'
import DatePicker from '../../../../../components/shared/date-picker/date-picker.component'
import { VARIANTS } from '../../../../../components/shared/date-picker/date-picker.constants'
import Loading from '../../../../../components/shared/loading/loading.component'
import { commonStyles } from '../../../../../styles/common.styles'

import { handleStylesWithProps, styles } from '../../table.styles'
import { FILTER_DEFAULT_VALUE, FILTER_SELECT_TYPES } from '../../table.constants'

const TableFilterMobileSelect = ({
  name,
  options,
  placeholder,
  filterStyles,
  type = FILTER_SELECT_TYPES.select,
  isFiltersLoading,
  muiSelectProps,
  disabled,
  initialValue,
  filterResetLabel,
}) => {
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('md'))
  const [anchorEl, setAnchorEl] = useState(null)
  const [isSelectOpen, setIsSelectOpen] = useState(false)
  const { control } = useFormContext()
  const {
    field: { value, onChange },
    fieldState,
  } = useController({
    name,
    control,
    defaultValue: initialValue ?? FILTER_DEFAULT_VALUE,
  })

  const validOptions = (options || []).filter(
    (option) => option && option.value !== undefined && option.value !== null
  )

  const renderValue = useCallback(() => {
    return (
      <Box sx={styles.flexSpaceBetween}>
        <Box sx={styles.filterSelect.renderValue}>
          <Typography
            typography="header4"
            sx={{
              ...styles.filterSelect.mobileValue,
            }}
          >
            {type === FILTER_SELECT_TYPES.date
              ? value && format(new Date(value), getSiteDateFormat('medium'))
              : validOptions.find((option) => option.value === value)?.label || value}
          </Typography>
        </Box>
      </Box>
    )
  }, [value, validOptions, type])

  const handleOpen = (event) => {
    if (type === FILTER_SELECT_TYPES.select) {
      setIsSelectOpen(true)
    } else {
      setAnchorEl(event.currentTarget)
    }
  }

  const handleClose = () => {
    if (type === FILTER_SELECT_TYPES.select) {
      setIsSelectOpen(false)
    } else {
      setAnchorEl(null)
    }
  }

  const handleChange = (e) => {
    if (type === FILTER_SELECT_TYPES.select) {
      onChange(e.target.value)
      setIsSelectOpen(false)
    }
  }

  const isOpen = Boolean(anchorEl)
  const id = isOpen ? 'simple-popover' : undefined
  const stylesWithProps = handleStylesWithProps({ filterSelectType: type })
  const isLoadingFilters = isFiltersLoading && validOptions.length === 0

  const isLabelShrinked = Boolean(value) || fieldState.isFocused

  return (
    <FormControl fullWidth variant="outlined">
      <InputLabel shrink={isLabelShrinked} error={fieldState.error} sx={{ left: '-6px' }}>
        {placeholder}
      </InputLabel>
      <MuiSelect
        onChange={handleChange}
        value={value}
        sx={[
          styles.filterSelect[isMobile ? 'mobileSelect' : 'desktopSelect'],
          stylesWithProps.filterSelect.select,
          filterStyles,
        ]}
        test-id={`table-filter-select-MuiSelect`}
        renderValue={renderValue}
        open={isSelectOpen}
        onOpen={handleOpen}
        aria-describedby={id}
        onClose={handleClose}
        MenuProps={muiSelectProps}
        notched={isLabelShrinked}
        disabled={disabled}
        input={<OutlinedInput notched={isLabelShrinked} label={`${placeholder}  `} />}
      >
        {!isLoadingFilters && <MenuItem value={''}>{filterResetLabel || placeholder}</MenuItem>}
        {isLoadingFilters ? (
          <MenuItem disabled>
            <Box sx={[commonStyles.fullWidth, commonStyles.centeredFlexRow]}>
              <Loading label={t('common:components.table.loading')} />
            </Box>
          </MenuItem>
        ) : (
          validOptions.map((option, idx) => (
            <MenuItem key={option.value} value={option.value} test-id={`${option.value}.${idx}`}>
              {option.label}
            </MenuItem>
          ))
        )}
      </MuiSelect>
      {value || value === false ? (
        <Box sx={styles.filterSelect.clearButtonMobile}>
          <IconButton
            onClick={() => onChange(FILTER_DEFAULT_VALUE)}
            size="small"
            sx={styles.filterSelect.clearButton}
          >
            <SvgIcon component={ClearSearchIcon} />
          </IconButton>
        </Box>
      ) : null}
      <Popover
        open={isOpen}
        id={id}
        test-id={`table-filter-select-popover`}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <DatePicker variant={VARIANTS.DAY} value={value} onChange={onChange} />
      </Popover>
    </FormControl>
  )
}

TableFilterMobileSelect.propTypes = {
  name: PropTypes.string.isRequired,
  filterStyles: PropTypes.object,
  filterContainerStyles: PropTypes.object,
  muiSelectProps: PropTypes.object,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    })
  ),
  placeholder: PropTypes.string,
  type: PropTypes.oneOf(Object.values(FILTER_SELECT_TYPES)),
  isFiltersLoading: PropTypes.bool,
  disabled: PropTypes.bool,
  initialValue: PropTypes.string,
  filterResetLabel: PropTypes.string,
}

export default TableFilterMobileSelect
